/* ==============================================================================
   Configurações do usuário — áudio, dispositivos e aparência
   ==============================================================================
   O que separa esta tela de uma lista de controles
   ===============================================
   Cada controle de áudio tem EFEITO AUDÍVEL IMEDIATO, com o medidor ao vivo ao
   lado. Ajustar um gate de ruído sem ouvir o resultado é adivinhação — e o
   sintoma de errar é a voz cortando no meio das palavras, que a pessoa só
   descobre numa reunião.

   Por isso: o pipeline roda enquanto esta tela está aberta, mesmo fora de
   chamada, e o medidor mostra o sinal DEPOIS de todo o processamento.

   Capacidade ausente é DESABILITADA COM MOTIVO
   ===========================================
   O seletor de fone não existe no Firefox nem no Safari. Escondê-lo faria
   parecer que o produto não tem a função; mostrar um seletor que não faz nada é
   pior. A terceira opção — desabilitado com a explicação ao lado — é a única que
   informa.
   ============================================================================== */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  AudioPipeline,
  DEFAULT_AUDIO_SETTINGS,
  explainMissing,
  type AudioSettings,
  type DeviceList,
  type PlatformCapabilities,
  type VoiceEngine,
} from '@concord/core';
import { SettingsShell, type SettingsNavItem } from '@concord/ui';

const STORAGE_KEY = 'concord.audio.v1';

/**
 * Configuração de áudio persistida localmente.
 *
 * `localStorage` e não o servidor, e a escolha é deliberada: `deviceId` é
 * específico da MÁQUINA. Sincronizar pelo servidor faria o notebook tentar usar
 * o microfone do desktop, e a resolução por `deviceId` falharia silenciosamente
 * caindo no dispositivo padrão. O que faz sentido sincronizar são os parâmetros
 * (limiar do gate, ganho), e isso vai para `user_settings` numa versão futura.
 */
function loadSettings(): AudioSettings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === null) return DEFAULT_AUDIO_SETTINGS;
    return { ...DEFAULT_AUDIO_SETTINGS, ...(JSON.parse(raw) as Partial<AudioSettings>) };
  } catch {
    return DEFAULT_AUDIO_SETTINGS;
  }
}

function saveSettings(settings: AudioSettings): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch {
    // Modo privado e cota cheia lançam. Perder a preferência é aceitável;
    // impedir de fechar a tela não é.
  }
}

export interface UserSettingsProps {
  /** Motor ativo, quando há chamada em andamento. */
  voiceEngine: VoiceEngine | null;
  capabilities: PlatformCapabilities;
  onClose: () => void;
}

type Tab = 'audio' | 'video' | 'appearance';

/*
 * A navegacao vira DADO, e nao mais tres botoes escritos a mao.
 *
 * O ganho nao e economia de JSX -- e o subtitulo. A referencia tem duas linhas
 * por item ("Geral / Aparencia Idioma Inicializacao"), e o motivo e pratico:
 * "Video e tela" nao diz se o limite de banda esta ali dentro, e a pessoa abre
 * as tres abas procurando. O subtitulo lista o que ha dentro, entao a escolha
 * acontece NA navegacao em vez de por tentativa.
 *
 * Fora do componente porque e constante: dentro, seria um array novo a cada
 * render e o `SettingsShell` recalcularia a lista sem motivo.
 */
const NAV: ReadonlyArray<SettingsNavItem> = [
  {
    id: 'audio',
    title: 'Voz e áudio',
    subtitle: 'Dispositivos · Ruído · Portão · Teste',
  },
  {
    id: 'video',
    title: 'Vídeo e tela',
    subtitle: 'Câmera · Compartilhamento · Limite de banda',
  },
  {
    id: 'appearance',
    title: 'Aparência',
    subtitle: 'Tema · Cor · Densidade · Contraste',
  },
];

export function UserSettings({
  voiceEngine,
  capabilities,
  onClose,
}: UserSettingsProps) {
  const [tab, setTab] = useState<Tab>('audio');
  const [settings, setSettings] = useState<AudioSettings>(loadSettings);
  const [devices, setDevices] = useState<DeviceList | null>(null);
  const [level, setLevel] = useState({ rms: 0, peak: 0 });
  const [testing, setTesting] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  /** Pipeline exclusivo do teste, separado do da chamada. */
  const testPipeline = useRef<AudioPipeline | null>(null);

  useEffect(() => {
    void AudioPipeline.listDevices().then(setDevices).catch(() => setDevices(null));

    /*
     * Re-enumera quando o hardware muda.
     *
     * Conectar um headset USB no meio da configuração é o caso comum de uso
     * desta tela. Sem este ouvinte, o dispositivo novo não apareceria até
     * recarregar a página — e a pessoa concluiria que o produto não o reconhece.
     */
    const onDeviceChange = () => {
      void AudioPipeline.listDevices().then(setDevices).catch(() => undefined);
    };
    navigator.mediaDevices?.addEventListener('devicechange', onDeviceChange);
    return () => {
      navigator.mediaDevices?.removeEventListener('devicechange', onDeviceChange);
    };
  }, []);

  useEffect(() => {
    saveSettings(settings);
  }, [settings]);

  /* ---- Encerra o teste ao sair da tela ---- */
  useEffect(
    () => () => {
      void testPipeline.current?.stop();
      testPipeline.current = null;
    },
    [],
  );

  const patch = useCallback(
    async (next: Partial<AudioSettings>) => {
      setSettings((current) => ({ ...current, ...next }));

      // Aplica no pipeline de TESTE, se ligado, e no da CHAMADA, se houver. Os
      // dois existem ao mesmo tempo quando alguém abre as configurações durante
      // uma chamada — e ajustar só um faria o teste soar diferente da chamada.
      testPipeline.current?.update(next);
      await voiceEngine?.updateAudioSettings(next);
    },
    [voiceEngine],
  );

  const startTest = useCallback(async () => {
    if (testPipeline.current !== null) return;
    const pipeline = new AudioPipeline();
    try {
      /*
       * `start()` dentro do clique, e por isso o botão existe.
       *
       * O `AudioContext` nasce suspenso; só um gesto do usuário o retoma.
       * Iniciar o teste automaticamente ao abrir a tela produziria um medidor
       * parado sem nenhuma mensagem de erro.
       */
      await pipeline.start(settings);
      testPipeline.current = pipeline;
      pipeline.onLevel((next) => setLevel({ rms: next.rms, peak: next.peak }));
      setTesting(true);
      setNotice(null);
    } catch (error) {
      setNotice(
        error instanceof Error && error.name === 'NotAllowedError'
          ? 'Permissão de microfone negada. Libere o acesso para testar.'
          : 'Não foi possível acessar o microfone.',
      );
    }
  }, [settings]);

  const stopTest = useCallback(async () => {
    await testPipeline.current?.stop();
    testPipeline.current = null;
    setTesting(false);
    setLevel({ rms: 0, peak: 0 });
  }, []);

  const outputExplanation = explainMissing('canSelectOutputDevice', capabilities);

  return (
    /*
     * A casca de duas colunas vem de `packages/ui`, e nao mais escrita aqui.
     *
     * Ela estava construida e nao ligada -- este arquivo mantinha a propria
     * navegacao de tres botoes de uma linha. O que muda:
     *
     *   · trilha de icones a esquerda, com o ativo marcado por barra de acento;
     *   · cada item com titulo E subtitulo (ver `NAV` acima);
     *   · rodape com o "voltar".
     *
     * Nao ha botao "salvar", e a ausencia e deliberada: TODA preferencia desta
     * tela se aplica na hora. Um "salvar alteracoes" faria a pessoa duvidar se o
     * que ela acabou de ouvir mudar tinha mesmo mudado.
     *
     * `variant="inline"` porque esta e uma rota de tela cheia. O padrao
     * `overlay` desenharia cortina e cartao flutuante sobre o app -- e o app nao
     * esta atras: ele foi substituido por esta tela.
     */
    <SettingsShell
      variant="inline"
      title="Configurações"
      items={NAV}
      activeItemId={tab}
      onSelectItem={(id) => setTab(id as Tab)}
      onClose={onClose}
    >
      <div className="min-w-0">
        {notice !== null && (
          <p
            role="alert"
            className="mb-4 px-3 py-2"
            style={{
              maxWidth: 560,
              borderRadius: 'var(--radius-md)',
              background: 'var(--color-danger-subtle)',
              fontSize: 'var(--text-xs)',
              color: 'var(--color-danger-on-subtle)',
            }}
          >
            {notice}
          </p>
        )}

        {tab === 'audio' && (
          <div className="flex flex-col gap-6" style={{ maxWidth: 620 }}>
            {/* ---- Dispositivos ---- */}
            <Group title="Dispositivos">
              <Field label="Microfone">
                <select
                  value={settings.inputDeviceId ?? ''}
                  onChange={(event) =>
                    void patch({
                      inputDeviceId:
                        event.target.value.length > 0 ? event.target.value : null,
                    })
                  }
                  style={selectStyle}
                >
                  <option value="">Padrão do sistema</option>
                  {devices?.inputs.map((device) => (
                    <option key={device.deviceId} value={device.deviceId}>
                      {device.label}
                    </option>
                  ))}
                </select>
                {devices?.inputs.some((d) => d.labelUnavailable) === true && (
                  <Hint>
                    Os nomes aparecem depois de conceder acesso ao microfone — o
                    navegador esconde o inventário de hardware até então.
                  </Hint>
                )}
              </Field>

              <Field label="Fone / saída">
                <select
                  value={settings.outputDeviceId ?? ''}
                  disabled={!capabilities.canSelectOutputDevice}
                  onChange={(event) =>
                    void patch({
                      outputDeviceId:
                        event.target.value.length > 0 ? event.target.value : null,
                    })
                  }
                  style={{
                    ...selectStyle,
                    opacity: capabilities.canSelectOutputDevice ? 1 : 0.5,
                  }}
                >
                  <option value="">Padrão do sistema</option>
                  {devices?.outputs.map((device) => (
                    <option key={device.deviceId} value={device.deviceId}>
                      {device.label}
                    </option>
                  ))}
                </select>
                {outputExplanation !== null && <Hint>{outputExplanation}</Hint>}
              </Field>
            </Group>

            {/* ---- Teste ao vivo ---- */}
            <Group title="Teste de microfone">
              <p
                className="mb-2"
                style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)' }}
              >
                O medidor mostra o sinal DEPOIS de todo o processamento — é
                exatamente o que os outros vão ouvir.
              </p>

              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => void (testing ? stopTest() : startTest())}
                  className="px-3 py-1.5 font-medium"
                  style={{
                    borderRadius: 'var(--radius-md)',
                    background: testing
                      ? 'var(--color-danger-subtle)'
                      : 'var(--color-accent-solid)',
                    color: testing
                      ? 'var(--color-danger-on-subtle)'
                      : 'var(--color-on-solid)',
                    fontSize: 'var(--text-xs)',
                  }}
                >
                  {testing ? 'Parar teste' : 'Testar microfone'}
                </button>

                <LevelMeter rms={level.rms} peak={level.peak} active={testing} />
              </div>
            </Group>

            {/* ---- Processamento nativo do navegador ---- */}
            <Group title="Processamento do navegador">
              <p
                className="mb-2"
                style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)' }}
              >
                Aplicado antes da nossa cadeia. Separado de propósito: a supressão
                do navegador às vezes corta o final das palavras em voz baixa, e o
                controle automático de ganho bombeia com ruído de fundo.
              </p>

              <Toggle
                label="Cancelamento de eco"
                hint="Só o navegador consegue fazer: exige o sinal do alto-falante como referência."
                checked={settings.echoCancellation}
                onChange={(value) => void patch({ echoCancellation: value })}
              />
              <Toggle
                label="Supressão de ruído nativa"
                hint="Combinável com o nosso gate. Em voz baixa, pode comer consoantes."
                checked={settings.noiseSuppression}
                onChange={(value) => void patch({ noiseSuppression: value })}
              />
              <Toggle
                label="Ganho automático"
                hint="Nivela o volume, mas amplifica o silêncio junto com a voz."
                checked={settings.autoGainControl}
                onChange={(value) => void patch({ autoGainControl: value })}
              />
            </Group>

            {/* ---- Cadeia própria ---- */}
            <Group title="Filtro de ruído">
              {!capabilities.hasAudioWorklet && (
                <Hint>{explainMissing('hasAudioWorklet', capabilities)}</Hint>
              )}

              <Toggle
                label="Corte de graves"
                hint={`Remove rumble abaixo de ${settings.highPass.frequencyHz} Hz — ventilador, ar-condicionado, mesa.`}
                checked={settings.highPass.enabled}
                onChange={(value) =>
                  void patch({ highPass: { ...settings.highPass, enabled: value } })
                }
              />

              <Slider
                label="Frequência de corte"
                unit="Hz"
                min={40}
                max={200}
                step={5}
                value={settings.highPass.frequencyHz}
                disabled={!settings.highPass.enabled}
                onChange={(value) =>
                  void patch({ highPass: { ...settings.highPass, frequencyHz: value } })
                }
              />

              <Toggle
                label="Gate de ruído"
                hint="Silencia quando você não está falando. É o que mais reduz incômodo numa reunião."
                checked={settings.gate.enabled}
                onChange={(value) =>
                  void patch({ gate: { ...settings.gate, enabled: value } })
                }
              />

              <Slider
                label="Limiar do gate"
                unit="dB"
                min={-70}
                max={-20}
                step={1}
                value={settings.gate.thresholdDb}
                disabled={!settings.gate.enabled}
                hint="Mais alto silencia mais, e corta voz baixa. Ajuste olhando o medidor: o gate deve abrir quando você fala normalmente."
                onChange={(value) =>
                  void patch({ gate: { ...settings.gate, thresholdDb: value } })
                }
              />

              <Slider
                label="Tempo de abertura"
                unit="ms"
                min={1}
                max={50}
                step={1}
                value={settings.gate.attackMs}
                disabled={!settings.gate.enabled}
                hint="Curto demais estala; longo demais come a consoante inicial."
                onChange={(value) =>
                  void patch({ gate: { ...settings.gate, attackMs: value } })
                }
              />

              <Slider
                label="Tempo de fechamento"
                unit="ms"
                min={20}
                max={500}
                step={10}
                value={settings.gate.releaseMs}
                disabled={!settings.gate.enabled}
                hint="Curto demais corta o final da palavra e a voz soa mordida."
                onChange={(value) =>
                  void patch({ gate: { ...settings.gate, releaseMs: value } })
                }
              />

              <Slider
                label="Retenção"
                unit="ms"
                min={0}
                max={800}
                step={50}
                value={settings.gate.holdMs}
                disabled={!settings.gate.enabled}
                hint="Mantém aberto nas pausas curtas entre palavras. Sem isso, o gate tremula."
                onChange={(value) =>
                  void patch({ gate: { ...settings.gate, holdMs: value } })
                }
              />

              {/*
                O controle do RNNoise aparece DESABILITADO com o motivo.

                Esconder daria a impressão de que o produto não tem a função;
                mostrar habilitado seria mentira, porque o worklet não existe
                ainda. Ver a nota no fim de packages/core/src/audio/pipeline.ts.
              */}
              <Slider
                label="Filtro neural (RNNoise)"
                unit="%"
                min={0}
                max={100}
                step={5}
                value={Math.round(settings.neuralSuppression * 100)}
                disabled
                hint="Ainda não implementado: o RNNoise processa quadros de 480 amostras e o AudioWorklet entrega 128, o que exige um ring buffer com teste de SNR sobre áudio real. O gate acima já cobre a maior parte do incômodo."
                onChange={() => undefined}
              />
            </Group>

            <Group title="Volume e dinâmica">
              <Slider
                label="Ganho de entrada"
                unit="dB"
                min={-12}
                max={12}
                step={1}
                value={settings.inputGainDb}
                hint="Suba se sua voz sai baixa. O limitador abaixo protege de saturar."
                onChange={(value) => void patch({ inputGainDb: value })}
              />

              <Toggle
                label="Compressor"
                hint="Nivela o volume: aproxima o sussurro do grito."
                checked={settings.compressor.enabled}
                onChange={(value) =>
                  void patch({ compressor: { ...settings.compressor, enabled: value } })
                }
              />

              <Toggle
                label="Limitador"
                hint="Proteção contra estalo de saturação. Recomendado sempre ligado."
                checked={settings.limiter.enabled}
                onChange={(value) =>
                  void patch({ limiter: { ...settings.limiter, enabled: value } })
                }
              />
            </Group>
          </div>
        )}

        {tab === 'video' && (
          <div className="flex flex-col gap-6" style={{ maxWidth: 620 }}>
            <Group title="Compartilhamento de tela">
              <p
                style={{
                  fontSize: 'var(--text-xs)',
                  lineHeight: 'var(--leading-relaxed)',
                  color: 'var(--color-text-tertiary)',
                }}
              >
                Cada tela adicionada é um fluxo de vídeo POR PARTICIPANTE. Duas
                telas numa chamada de oito pessoas são quatorze fluxos de upload
                saindo da sua conexão — é por isso que o governador de banda avisa
                antes de a segunda tela degradar a chamada de todos.
              </p>
              {explainMissing('canShareScreen', capabilities) !== null && (
                <Hint>{explainMissing('canShareScreen', capabilities)}</Hint>
              )}
              {explainMissing('canCaptureSystemAudio', capabilities) !== null && (
                <Hint>{explainMissing('canCaptureSystemAudio', capabilities)}</Hint>
              )}
            </Group>

            <Group title="Limite de upload">
              <p
                className="mb-2"
                style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)' }}
              >
                Por padrão o Concord mede a banda disponível e usa 85% dela. Fixe um
                teto menor se você divide a rede com outras pessoas.
              </p>
              <UploadCeiling voiceEngine={voiceEngine} />
            </Group>

            <Group title="Atalhos">
              {explainMissing('canRegisterGlobalHotkey', capabilities) !== null && (
                <Hint>{explainMissing('canRegisterGlobalHotkey', capabilities)}</Hint>
              )}
            </Group>
          </div>
        )}

        {tab === 'appearance' && <AppearanceSettings />}
      </div>
    </SettingsShell>
  );
}

/* ==============================================================================
   Peças
============================================================================== */

const selectStyle: React.CSSProperties = {
  width: '100%',
  maxWidth: 360,
  height: 32,
  padding: '0 8px',
  borderRadius: 'var(--radius-md)',
  background: 'var(--color-surface-sunken)',
  boxShadow: 'inset 0 1px 0 oklch(0% 0 0 / 0.24), 0 0 0 1px var(--color-border-default)',
  fontSize: 'var(--text-xs)',
  color: 'var(--color-text-primary)',
};

/*
 * `TabButton` foi REMOVIDO junto com a navegacao antiga.
 *
 * A navegacao agora vive no `SettingsShell`. Um botao de aba orfao aqui seria
 * uma segunda gramatica visual para a mesma coisa, esperando alguem usar por
 * engano -- e foi por isso que ele saiu em vez de ficar "por enquanto".
 */

function Group({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h2
        className="mb-2.5 font-semibold uppercase"
        style={{
          fontSize: 'var(--text-2xs)',
          letterSpacing: 'var(--tracking-wide)',
          color: 'var(--color-text-tertiary)',
        }}
      >
        {title}
      </h2>
      <div className="flex flex-col gap-3">{children}</div>
    </section>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span
        className="mb-1 block"
        style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-secondary)' }}
      >
        {label}
      </span>
      {children}
    </label>
  );
}

function Hint({ children }: { children: React.ReactNode }) {
  return (
    <p
      className="mt-1"
      style={{
        fontSize: 'var(--text-2xs)',
        lineHeight: 'var(--leading-normal)',
        color: 'var(--color-text-tertiary)',
      }}
    >
      {children}
    </p>
  );
}

function Toggle({
  label,
  hint,
  checked,
  onChange,
}: {
  label: string;
  hint?: string;
  checked: boolean;
  onChange: (value: boolean) => void;
}) {
  return (
    <div>
      <label className="flex cursor-pointer items-center gap-2.5">
        <input
          type="checkbox"
          checked={checked}
          onChange={(event) => onChange(event.target.checked)}
          className="sr-only"
        />
        <span
          aria-hidden="true"
          className="relative block shrink-0 transition-colors"
          style={{
            width: 32,
            height: 18,
            borderRadius: 'var(--radius-full)',
            background: checked
              ? 'var(--color-accent-solid)'
              : 'var(--color-surface-hover)',
          }}
        >
          <span
            className="absolute block transition-transform"
            style={{
              top: 2,
              left: 2,
              width: 14,
              height: 14,
              borderRadius: 'var(--radius-full)',
              background: 'var(--color-on-solid)',
              transform: checked ? 'translateX(14px)' : 'none',
            }}
          />
        </span>
        <span
          style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-secondary)' }}
        >
          {label}
        </span>
      </label>
      {hint !== undefined && <Hint>{hint}</Hint>}
    </div>
  );
}

function Slider({
  label,
  unit,
  min,
  max,
  step,
  value,
  onChange,
  disabled = false,
  hint,
}: {
  label: string;
  unit: string;
  min: number;
  max: number;
  step: number;
  value: number;
  onChange: (value: number) => void;
  disabled?: boolean;
  hint?: string;
}) {
  return (
    <div style={{ opacity: disabled ? 0.5 : 1 }}>
      <div className="flex items-baseline justify-between">
        <label
          style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-secondary)' }}
        >
          {label}
        </label>
        <span
          style={{
            fontSize: 'var(--text-2xs)',
            fontFamily: 'var(--font-mono)',
            color: 'var(--color-text-tertiary)',
          }}
        >
          {value} {unit}
        </span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(Number(event.target.value))}
        className="mt-1 w-full"
        style={{ maxWidth: 360, accentColor: 'var(--color-accent-solid)' }}
      />
      {hint !== undefined && <Hint>{hint}</Hint>}
    </div>
  );
}

/**
 * Medidor de nível.
 *
 * Duas barras sobrepostas: RMS (o volume percebido) e PICO (o transiente). Só o
 * RMS esconderia saturação — o pico bate no teto antes de o RMS chegar perto, e
 * é ele que revela que o ganho está alto demais.
 */
function LevelMeter({
  rms,
  peak,
  active,
}: {
  rms: number;
  peak: number;
  active: boolean;
}) {
  return (
    <div className="min-w-0 flex-1">
      <div
        className="relative overflow-hidden"
        style={{
          height: 10,
          borderRadius: 'var(--radius-full)',
          background: 'var(--color-surface-sunken)',
          maxWidth: 280,
        }}
      >
        <span
          className="absolute inset-y-0 left-0 transition-all"
          style={{
            width: `${Math.round(rms * 100)}%`,
            background:
              peak > 0.95
                ? 'var(--color-danger-solid)'
                : 'var(--color-voice-speaking)',
            transitionDuration: 'var(--duration-instant)',
          }}
        />
        <span
          className="absolute inset-y-0"
          style={{
            left: `${Math.round(peak * 100)}%`,
            width: 2,
            background: 'var(--color-text-primary)',
          }}
        />
      </div>
      <p
        className="mt-1"
        style={{ fontSize: 'var(--text-2xs)', color: 'var(--color-text-tertiary)' }}
      >
        {!active
          ? 'Inicie o teste para ver o nível.'
          : peak > 0.95
            ? 'Saturando — reduza o ganho de entrada.'
            : rms < 0.05
              ? 'Sinal muito baixo. Fale normalmente; se continuar baixo, suba o ganho.'
              : 'Nível bom.'}
      </p>
    </div>
  );
}

function UploadCeiling({ voiceEngine }: { voiceEngine: VoiceEngine | null }) {
  const [ceiling, setCeiling] = useState<number | null>(() => {
    const raw = localStorage.getItem('concord.upload.ceiling');
    return raw !== null ? Number(raw) : null;
  });

  const apply = useCallback(
    (value: number | null) => {
      setCeiling(value);
      if (value === null) localStorage.removeItem('concord.upload.ceiling');
      else localStorage.setItem('concord.upload.ceiling', String(value));
      voiceEngine?.setUploadCeiling(value);
    },
    [voiceEngine],
  );

  return (
    <div className="flex items-center gap-3">
      <select
        value={ceiling === null ? '' : String(ceiling)}
        onChange={(event) =>
          apply(event.target.value.length > 0 ? Number(event.target.value) : null)
        }
        style={{ ...selectStyle, maxWidth: 220 }}
      >
        <option value="">Automático (medido)</option>
        <option value="2000">2 Mbps</option>
        <option value="5000">5 Mbps</option>
        <option value="10000">10 Mbps</option>
        <option value="20000">20 Mbps</option>
        <option value="50000">50 Mbps</option>
      </select>
    </div>
  );
}

/**
 * Aparência: tema, densidade e contraste.
 *
 * Escreve atributos no `<html>`, que é onde os tokens de `theme.css` os leem.
 * Trocar tema sem rebuild e sem reload é o ponto de todo o sistema de tokens
 * viver em custom properties — ver o cabeçalho de packages/ui/src/styles/theme.css.
 */
function AppearanceSettings() {
  const [theme, setTheme] = useState(
    () => document.documentElement.dataset.theme ?? 'dark',
  );
  const [density, setDensity] = useState(
    () => document.documentElement.dataset.density ?? 'comfortable',
  );
  const [contrast, setContrast] = useState(
    () => document.documentElement.dataset.contrast ?? 'normal',
  );

  useEffect(() => {
    // `dark` é a ausência do atributo: o bloco `@theme` já é o tema escuro, e
    // `[data-theme='dark']` não existe no CSS.
    if (theme === 'dark') delete document.documentElement.dataset.theme;
    else document.documentElement.dataset.theme = theme;
    localStorage.setItem('concord.theme', theme);
  }, [theme]);

  useEffect(() => {
    document.documentElement.dataset.density = density;
    localStorage.setItem('concord.density', density);
  }, [density]);

  useEffect(() => {
    if (contrast === 'normal') delete document.documentElement.dataset.contrast;
    else document.documentElement.dataset.contrast = contrast;
    localStorage.setItem('concord.contrast', contrast);
  }, [contrast]);

  return (
    <div className="flex flex-col gap-6" style={{ maxWidth: 620 }}>
      <Group title="Tema">
        <Field label="Escolha seu tema">
          {/*
            Pastilhas em vez de <select>.
            ----------------------------
            Três opções cabem lado a lado, e ver as três de uma vez é o ponto:
            escolher tema é comparar, não escolher de uma lista. Um `select`
            esconde duas das três atrás de um clique.
          */}
          <div className="flex flex-wrap gap-2">
            {[
              { value: 'dark', label: 'Escuro' },
              { value: 'light', label: 'Claro' },
              { value: 'neon', label: 'Neon' },
            ].map((option) => {
              const selected = theme === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setTheme(option.value)}
                  aria-pressed={selected}
                  className="px-3.5 py-1.5 font-medium transition-all"
                  style={{
                    borderRadius: 'var(--radius-full)',
                    fontSize: 'var(--text-xs)',
                    background: selected
                      ? 'var(--color-accent-solid)'
                      : 'var(--color-surface-hover)',
                    color: selected
                      ? 'var(--color-on-solid)'
                      : 'var(--color-text-secondary)',
                    boxShadow: selected ? 'var(--glow-medium)' : 'none',
                  }}
                >
                  {option.label}
                  {selected && (
                    <span
                      style={{ marginLeft: 6, fontSize: 'var(--text-2xs)', opacity: 0.85 }}
                    >
                      selecionado
                    </span>
                  )}
                </button>
              );
            })}
          </div>
          <Hint>
            O Neon é o Escuro com fundo mais profundo e brilho bem mais intenso. O
            TEXTO é idêntico nos três de propósito: aumentar a saturação da letra
            junto com o resto é o que faz um tema neon virar ilegível.
          </Hint>
        </Field>
      </Group>

      <Group title="Densidade">
        <Field label="Espaçamento da interface">
          <select
            value={density}
            onChange={(event) => setDensity(event.target.value)}
            style={selectStyle}
          >
            <option value="compact">Compacta</option>
            <option value="comfortable">Confortável</option>
            <option value="spacious">Espaçosa</option>
          </select>
          <Hint>
            Multiplica a escala de espaço inteira. Compacta mostra mais mensagens
            por tela; espaçosa é mais fácil de ler por longos períodos.
          </Hint>
        </Field>
      </Group>

      <Group title="Acessibilidade">
        <Field label="Contraste">
          <select
            value={contrast}
            onChange={(event) => setContrast(event.target.value)}
            style={selectStyle}
          >
            <option value="normal">Normal</option>
            <option value="high">Alto</option>
          </select>
          <Hint>
            O contraste alto reforça bordas e texto secundário. Todos os pares de
            cor do tema normal já atendem WCAG AA — verificado por
            scripts/check-contrast.mjs.
          </Hint>
        </Field>
      </Group>
    </div>
  );
}
